import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useSessionComments } from '@/hooks/useSessionComments';
import { useAuth } from '@/hooks/useAuth';
import { useWorkspaceMembers } from '@/hooks/useWorkspaceMembers';
import { formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale';
import { MessageSquare, Send, Loader2, Trash2, AtSign } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { MarkdownEditor } from './MarkdownEditor';

interface SessionCommentsProps {
  sessionId: string;
  workspaceId: string;
}

export const SessionComments = ({ sessionId, workspaceId }: SessionCommentsProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { comments, loading, addComment, deleteComment } = useSessionComments(sessionId);
  const { members } = useWorkspaceMembers(workspaceId);
  
  const [newComment, setNewComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showMentionPopover, setShowMentionPopover] = useState(false);

  const extractMentions = (text: string): string[] => {
    const mentionRegex = /@\[([^\]]+)\]\(([^)]+)\)/g;
    const mentions: string[] = [];
    let match;
    
    while ((match = mentionRegex.exec(text)) !== null) {
      mentions.push(match[2]); // Extract user ID
    }
    
    return mentions;
  };

  const handleMention = (userId: string, userName: string) => {
    setNewComment(prev => prev + `@[${userName}](${userId}) `);
    setShowMentionPopover(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim() || !user) return;

    setIsSubmitting(true);
    try {
      const mentionedUsers = extractMentions(newComment);
      await addComment(newComment, mentionedUsers);
      setNewComment('');
      toast({
        title: 'Kommentar hinzugefügt',
        description: mentionedUsers.length > 0 
          ? `${mentionedUsers.length} Nutzer wurden benachrichtigt`
          : undefined,
      });
    } catch (error) {
      toast({
        title: 'Fehler',
        description: 'Kommentar konnte nicht hinzugefügt werden',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (commentId: string) => {
    try {
      await deleteComment(commentId);
      toast({
        title: 'Kommentar gelöscht',
      });
    } catch (error) {
      toast({
        title: 'Fehler',
        description: 'Kommentar konnte nicht gelöscht werden',
        variant: 'destructive',
      });
    }
  };

  const renderCommentText = (text: string) => {
    // Replace @[Name](userId) with styled mentions for markdown
    const textWithMentions = text.replace(/@\[([^\]]+)\]\(([^)]+)\)/g, '**@$1**');
    
    return (
      <div className="prose prose-sm dark:prose-invert max-w-none">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeSanitize]}
          components={{
            p: ({ node, ...props }) => (
              <p className="mb-1 leading-relaxed text-sm" {...props} />
            ),
            strong: ({ node, children, ...props }) => {
              // Check if this is a mention
              const text = String(children);
              if (text.startsWith('@')) {
                return (
                  <span className="bg-primary/10 text-primary px-1 rounded font-semibold" {...props}>
                    {children}
                  </span>
                );
              }
              return <strong {...props}>{children}</strong>;
            },
            code: ({ node, inline, ...props }: any) =>
              inline ? (
                <code
                  className="bg-muted px-1 py-0.5 rounded text-xs font-mono"
                  {...props}
                />
              ) : (
                <code
                  className="block bg-muted p-2 rounded text-xs font-mono overflow-x-auto my-1"
                  {...props}
                />
              ),
            ul: ({ node, ...props }) => (
              <ul className="list-disc list-inside my-1 space-y-0.5 text-sm" {...props} />
            ),
            ol: ({ node, ...props }) => (
              <ol className="list-decimal list-inside my-1 space-y-0.5 text-sm" {...props} />
            ),
            blockquote: ({ node, ...props }) => (
              <blockquote
                className="border-l-2 border-border pl-2 italic my-1 text-muted-foreground text-sm"
                {...props}
              />
            ),
            a: ({ node, ...props }) => (
              <a
                className="text-primary hover:underline"
                target="_blank"
                rel="noopener noreferrer"
                {...props}
              />
            ),
          }}
        >
          {textWithMentions}
        </ReactMarkdown>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold flex items-center gap-2 text-foreground">
          <MessageSquare className="h-4 w-4" />
          Kommentare ({comments.length})
        </h3>
      </div>
      
      <ScrollArea className="h-[300px] pr-2">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : comments.length === 0 ? (
          <div className="text-center py-8 text-sm text-muted-foreground">
            Noch keine Kommentare. Sei der Erste!
          </div>
        ) : (
          <div className="space-y-4">
            {comments.map((comment) => (
              <div key={comment.id} className="flex gap-3 group">
                <Avatar className="h-8 w-8 flex-shrink-0">
                  <AvatarImage src={comment.profiles?.avatar_url || undefined} />
                  <AvatarFallback>
                    {comment.profiles?.name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || '?'}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-sm">
                      {comment.profiles?.name || (comment.user_id === user?.id ? 'Du' : 'Unbekannter Nutzer')}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {formatDistanceToNow(new Date(comment.created_at), {
                        locale: de,
                        addSuffix: true,
                      })}
                    </span>
                    {comment.id.startsWith('temp-') && (
                      <span className="text-xs text-muted-foreground italic">Wird gesendet...</span>
                    )}
                  </div>
                  <div className="text-sm text-foreground break-words">
                    {renderCommentText(comment.content)}
                  </div>
                </div>
                {comment.user_id === user?.id && !comment.id.startsWith('temp-') && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8 p-0"
                    onClick={() => handleDelete(comment.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </ScrollArea>

      <form onSubmit={handleSubmit} className="space-y-3 pt-3 border-t border-border">
        <MarkdownEditor
          value={newComment}
          onChange={setNewComment}
          placeholder="Schreibe einen Kommentar... (nutze @ um jemanden zu erwähnen)"
          disabled={isSubmitting}
          minRows={2}
        />
        <div className="flex items-center justify-between">
          <Popover open={showMentionPopover} onOpenChange={setShowMentionPopover}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-muted-foreground hover:text-foreground"
              >
                <AtSign className="h-4 w-4 mr-1" />
                Erwähnen
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-2 z-[100]" align="start">
              <div className="text-xs font-medium mb-2 text-muted-foreground">
                Team-Mitglieder erwähnen
              </div>
              <ScrollArea className="h-[200px]">
                <div className="space-y-1">
                  {members.map((member) => (
                    <button
                      key={member.user_id}
                      type="button"
                      onClick={() => handleMention(member.user_id, member.profiles?.name || 'Unbekannt')}
                      className="w-full flex items-center gap-2 p-2 rounded hover:bg-accent transition-colors text-left"
                    >
                      <Avatar className="h-6 w-6">
                        <AvatarImage src={member.profiles?.avatar_url || undefined} />
                        <AvatarFallback className="text-xs">
                          {member.profiles?.name?.charAt(0)?.toUpperCase() || '?'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {member.profiles?.name || 'Unbekannter Nutzer'}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {member.role}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              </ScrollArea>
            </PopoverContent>
          </Popover>
          <Button
            type="submit"
            disabled={!newComment.trim() || isSubmitting}
            size="sm"
          >
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Send className="h-4 w-4 mr-2" />
            )}
            Senden
          </Button>
        </div>
      </form>
    </div>
  );
};
